/**
 * Team Page - Manage team members, invites, and roles
 * v4.28 - 2026-10-13 - G10-B (TEAM-ACCESS-ROLES-01): role and permissions come from the shared /api/me snapshot (no own
 *                      /api/me call, no role copy); a person who may not invite never calls the admin-only invites route;
 *                      the empty-state invite button follows inviteMembers; the invite form offers no role choice (the
 *                      server decides the invitable role); role labels come from the translation file; invite errors
 *                      show the server's reason code in words
 * v2.77 - 2026-02-05 - Fixed fetch('/api/me') to include X-Authorization header for Clerk JWT
 * v2.38 - Fixed credentials: 'include' for SWA auth - 2026-02-01
 * v2.33 - Added role-based permission checks - 2026-01-31
 * v2.27 - Full implementation with real API - 2026-01-30
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  PersonAdd as InviteIcon,
  Email as EmailIcon,
  AccessTime as PendingIcon,
  Refresh as ResendIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  CheckCircle as ActiveIcon
} from '@mui/icons-material';
import TopBar from '../components/TopBar';
import { teamService, TeamMember, TeamInvite } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';

const Team: React.FC = () => {
  const { t } = useTranslation();
  // v4.28 - G10-B: permissions of the chosen company, read once in the layout
  const { hasPermission } = usePermissions();
  const roleLabel = (role: string) => t(`teamAccess.roles.${role}`, { defaultValue: role });
  const roleDescription = (role: string) => t(`teamAccess.roleDescriptions.${role}`, { defaultValue: '' });
  const canViewTeam = hasPermission('viewTeam');
  const canInviteMembers = hasPermission('inviteMembers');
  const canRemoveMembers = hasPermission('removeMembers');
  const canChangeRoles = hasPermission('changeRoles');

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Invite modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Member menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  // Role change dialog
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState<string>('member');
  const [changingRole, setChangingRole] = useState(false);

  // Remove confirmation
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Load team data
  const loadTeamData = useCallback(async () => {
    if (!canViewTeam) { setLoading(false); return; } // v4.28 - G10-B: no call the server would refuse
    try {
      setLoading(true);
      setError(null);
      // v4.28 - G10-B: pending invites is an admin-only route; a person without inviteMembers never calls it
      const [membersRes, invitesRes] = await Promise.all([
        teamService.getMembers(),
        canInviteMembers ? teamService.getPendingInvites() : Promise.resolve({ invites: [] as TeamInvite[] }),
      ]);
      setMembers(membersRes.members);
      setInvites(invitesRes.invites);
    } catch (err: any) {
      setError(err.userMessage || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [canInviteMembers, canViewTeam]);

  useEffect(() => {
    loadTeamData();
  }, [loadTeamData]);

  // Get initials from email
  const getInitials = (email: string, name?: string): string => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  // Handle invite
  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
      setInviteError('Please enter a valid email address');
      return;
    }

    try {
      setInviting(true);
      setInviteError(null);
      await teamService.invite({
        email: inviteEmail.trim(),
        message: inviteMessage.trim() || undefined
      });
      setInviteModalOpen(false);
      setInviteEmail('');
      setInviteMessage('');
      await loadTeamData();
    } catch (err: any) {
      // v4.28 - G10-B: say what the server refused, in words from the translation file
      const code = err?.response?.data?.error;
      // 502 invite_email_not_sent: the invitation exists, only the email failed - show it in the list so it can be resent
      if (code === 'invite_email_not_sent') await loadTeamData();
      setInviteError(code ? t(`teamAccess.inviteErrors.${code}`, { defaultValue: err.userMessage || t('teamAccess.inviteErrors.default') }) : (err.userMessage || t('teamAccess.inviteErrors.default')));
    } finally {
      setInviting(false);
    }
  };

  // Handle role change
  const handleRoleChange = async () => {
    if (!selectedMember) return;

    try {
      setChangingRole(true);
      await teamService.updateRole(selectedMember.id, newRole);
      setRoleDialogOpen(false);
      setMenuAnchor(null);
      await loadTeamData();
    } catch (err: any) {
      setError(err.userMessage || 'Failed to change role');
    } finally {
      setChangingRole(false);
    }
  };

  // Handle remove member
  const handleRemove = async () => {
    if (!selectedMember) return;

    try {
      setRemoving(true);
      await teamService.removeMember(selectedMember.id);
      setRemoveDialogOpen(false);
      setMenuAnchor(null);
      await loadTeamData();
    } catch (err: any) {
      setError(err.userMessage || 'Failed to remove member');
    } finally {
      setRemoving(false);
    }
  };

  // Handle resend invite
  const handleResendInvite = async (inviteId: string) => {
    try {
      await teamService.resendInvite(inviteId);
      await loadTeamData();
    } catch (err: any) {
      // v4.28 - G10-B: 502 invite_email_not_sent (and the other reason codes) in words from the translation file
      const code = err?.response?.data?.error;
      setError(code ? t(`teamAccess.inviteErrors.${code}`, { defaultValue: err.userMessage || 'Failed to resend invite' }) : (err.userMessage || 'Failed to resend invite'));
    }
  };

  // Handle cancel invite
  const handleCancelInvite = async (inviteId: string) => {
    try {
      await teamService.cancelInvite(inviteId);
      await loadTeamData();
    } catch (err: any) {
      setError(err.userMessage || 'Failed to cancel invite');
    }
  };

  // Open member menu
  const handleMemberMenuClick = (event: React.MouseEvent<HTMLElement>, member: TeamMember) => {
    setMenuAnchor(event.currentTarget);
    setSelectedMember(member);
  };

  // Open role change dialog
  const openRoleDialog = () => {
    if (selectedMember) {
      setNewRole(selectedMember.role);
      setRoleDialogOpen(true);
    }
    setMenuAnchor(null);
  };

  // Open remove dialog
  const openRemoveDialog = () => {
    setRemoveDialogOpen(true);
    setMenuAnchor(null);
  };

  // Format date
  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Active members (not pending)
  const activeMembers = members.filter(m => m.status === 'active');
  // v4.28 - G10-B: roles are listed from what the company actually has, never from a copy in the portal
  const presentRoles = Array.from(new Set([...members.map((m) => m.role), ...invites.map((i) => i.role)])).filter(Boolean);

  // v4.28 - G10-B: a screen the permission map says no to is not shown
  if (!canViewTeam) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert severity="warning">{t('teamAccess.accessDenied')}</Alert>
      </Box>
    );
  }

  return (
    // v2.28 - Mobile responsive padding - 2026-03-20
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <TopBar
        title="Team"
        subtitle={`${activeMembers.length} member${activeMembers.length !== 1 ? 's' : ''}`}
        actionButton={canInviteMembers ? {
          label: 'Invite Member',
          onClick: () => setInviteModalOpen(true),
          startIcon: <InviteIcon />
        } : undefined}
      />

      <Container maxWidth="md" sx={{ mt: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Card sx={{ bgcolor: '#BBCDE2' }}>
              {/* v2.28 - scrollable tabs on mobile */}
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  px: 2,
                  '& .MuiTab-root': { color: '#003366' },
                  '& .Mui-selected': { color: '#008080', fontWeight: 700 }
                }}
              >
                <Tab label={`Members (${activeMembers.length})`} />
                {canInviteMembers && <Tab label={`Pending Invites (${invites.length})`} />}
              </Tabs>

              <Divider />

              {/* Members Tab */}
              {tabValue === 0 && (
                <CardContent>
                  {activeMembers.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">No team members yet</Typography>
                      {canInviteMembers && (
                        <Button
                          variant="contained"
                          startIcon={<InviteIcon />}
                          onClick={() => setInviteModalOpen(true)}
                          sx={{ mt: 2, bgcolor: '#008080', '&:hover': { bgcolor: '#006666' } }}
                        >
                          Invite Your First Team Member
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <List>
                      {activeMembers.map((member, index) => (
                        <React.Fragment key={member.id}>
                          {index > 0 && <Divider />}
                          <ListItem sx={{ py: 2 }}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: '#003366' }}>
                                {getInitials(member.email, member.name)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366' }}>
                                    {member.name || member.email}
                                  </Typography>
                                  <Chip
                                    label={roleLabel(member.role)}
                                    size="small"
                                    color="default"
                                  />
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 0.5 }}>
                                  {member.name && <Typography variant="body2" color="text.secondary">{member.email}</Typography>}
                                  <Typography variant="caption" color="text.secondary">
                                    Joined {formatDate(member.joinedAt)}
                                  </Typography>
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              {/* v2.33: Only show menu if user can change roles or remove members */}
                              {member.role !== 'owner' && (canChangeRoles || canRemoveMembers) && (
                                <IconButton onClick={(e) => handleMemberMenuClick(e, member)}>
                                  <MoreIcon />
                                </IconButton>
                              )}
                            </ListItemSecondaryAction>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              )}

              {/* Pending Invites Tab */}
              {tabValue === 1 && (
                <CardContent>
                  {invites.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">No pending invites</Typography>
                    </Box>
                  ) : (
                    <List>
                      {invites.map((invite, index) => (
                        <React.Fragment key={invite.id}>
                          {index > 0 && <Divider />}
                          <ListItem sx={{ py: 2 }}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: invite.expired ? '#999' : '#F4A261' }}>
                                <PendingIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: invite.expired ? '#999' : '#003366' }}>
                                    {invite.email}
                                  </Typography>
                                  <Chip
                                    label={roleLabel(invite.role)}
                                    size="small"
                                    color="default"
                                    variant="outlined"
                                  />
                                  {invite.expired && (
                                    <Chip label="Expired" size="small" color="error" />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary">
                                  Invited {formatDate(invite.invitedAt)} by {invite.invitedBy}
                                </Typography>
                              }
                            />
                            {/* v2.33: Only show actions if user can invite members */}
                            {canInviteMembers && (
                            <ListItemSecondaryAction>
                              <Tooltip title="Resend invite">
                                <IconButton
                                  onClick={() => handleResendInvite(invite.id)}
                                  sx={{ color: '#008080' }}
                                >
                                  <ResendIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel invite">
                                <IconButton
                                  onClick={() => handleCancelInvite(invite.id)}
                                  sx={{ color: '#dc3545' }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                            )}
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Role descriptions */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ color: '#003366', mb: 2 }}>Role Permissions</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {presentRoles.map((role) => (
                  <Chip
                    key={role}
                    label={`${roleLabel(role)}: ${roleDescription(role)}`}
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
            </Box>
          </>
        )}
      </Container>

      {/* Member Menu - v2.33: Show items based on permissions */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {canChangeRoles && (
          <MenuItem onClick={openRoleDialog}>
            <EditIcon sx={{ mr: 1, fontSize: 20 }} /> Change Role
          </MenuItem>
        )}
        {canRemoveMembers && (
          <MenuItem onClick={openRemoveDialog} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1, fontSize: 20 }} /> Remove from Team
          </MenuItem>
        )}
      </Menu>

      {/* Invite Modal */}
      <Dialog
        open={inviteModalOpen}
        onClose={() => !inviting && setInviteModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#003366', color: 'white' }}>
          Invite Team Member
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {inviteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {inviteError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="colleague@company.com"
            sx={{ mb: 3, mt: 1 }}
            disabled={inviting}
          />

          <TextField
            fullWidth
            label="Personal Message (optional)"
            multiline
            rows={2}
            value={inviteMessage}
            onChange={(e) => setInviteMessage(e.target.value)}
            placeholder="Add a personal message to the invite email..."
            disabled={inviting}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInviteModalOpen(false)} disabled={inviting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={inviting}
            startIcon={inviting ? <CircularProgress size={16} /> : <EmailIcon />}
            sx={{ bgcolor: '#008080', '&:hover': { bgcolor: '#006666' } }}
          >
            {inviting ? 'Sending...' : 'Send Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog
        open={roleDialogOpen}
        onClose={() => !changingRole && setRoleDialogOpen(false)}
      >
        <DialogTitle>Change Role</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Change role for <strong>{selectedMember?.email}</strong>
          </Typography>
          <FormControl fullWidth>
            <InputLabel>New Role</InputLabel>
            <Select
              value={newRole}
              label="New Role"
              onChange={(e) => setNewRole(e.target.value)}
              disabled={changingRole}
            >
              {presentRoles.filter((r) => r !== 'owner').map((r) => (
                <MenuItem key={r} value={r}>{roleLabel(r)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)} disabled={changingRole}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRoleChange}
            disabled={changingRole}
            sx={{ bgcolor: '#008080', '&:hover': { bgcolor: '#006666' } }}
          >
            {changingRole ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={() => !removing && setRemoveDialogOpen(false)}
      >
        <DialogTitle sx={{ color: '#dc3545' }}>Remove Team Member</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{selectedMember?.email}</strong> from the team?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            They will lose access to all team data and resources.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)} disabled={removing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRemove}
            disabled={removing}
          >
            {removing ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Team;
